import { Header as ReactTableHeader } from '@tanstack/react-table';

import { Box, TdProps, Th, ThProps } from '@oaknorth/component-core-v2';

import { renderHeaderContents } from '../../flexRender';
import { horizontalPinningProps } from '../horizontalPinning';

import { SortIndicator } from './SortIndicator';

export interface HeaderProps<T> extends ThProps {
  /**
   * React Elements rendered inside <th>
   */
  children?: React.ReactNode;
  /**
   * Column object based on react-table
   */
  header: ReactTableHeader<T, unknown>;

  /**
   * Whether the header should fill its parent's width
   *
   * Useful in e.g. group headers that we want to center
   */
  fullWidth?: boolean;
}

export const Header = <T,>({ header, children, fullWidth, ...props }: HeaderProps<T>) => {
  const { isNumeric, minWidth, isPinnedHorizontal } = header.column.columnDef.meta || {};
  const canSort = header.column.getCanSort();
  const firstSortDirection = header.column.getFirstSortDir();
  const pinnedProps: Partial<TdProps> = isPinnedHorizontal ? horizontalPinningProps : {};

  const tableIsMultisorted = header.getContext().table.getState().sorting.length > 1;
  const columnSortIndex = header.column.getSortIndex();

  const columnIsSorted = header.column.getIsSorted() !== false;

  return (
    <Th
      data-testid={`table-header-${header.column.id}`}
      // This triggers the _groupHover style on SortIndicator when the whole header cell is hovered
      data-group
      colSpan={header.colSpan}
      isNumeric={isNumeric}
      minWidth={minWidth}
      onClick={header.column.getToggleSortingHandler()}
      cursor={canSort ? 'pointer' : ''}
      width={`${header.column.getSize()}px`}
      {...pinnedProps}
      {...props}
    >
      {header.isPlaceholder ? null : (
        <Box
          as="span"
          display="inline-flex"
          flexDirection={isNumeric ? 'row-reverse' : 'row'}
          gap={1}
          alignItems="center"
          width={fullWidth ? 'full' : undefined}
        >
          <Box as="span" minWidth={0} width={fullWidth ? 'full' : undefined}>
            {children ? children : renderHeaderContents(header)}
          </Box>
          {canSort && (
            <SortIndicator
              isSorted={columnIsSorted}
              isSortedDesc={header.column.getIsSorted() === 'desc'}
              firstSortDirection={firstSortDirection}
              showSortPriority={tableIsMultisorted && columnIsSorted}
              sortIndex={columnSortIndex}
              isNumeric={isNumeric}
              columnId={header.column.id}
            />
          )}
        </Box>
      )}
    </Th>
  );
};
